/**
 * Header: sticky behaviour, the mobile drawer, and desktop dropdowns.
 */
class RanaHeader extends HTMLElement {
  connectedCallback() {
    this.drawer = this.querySelector('[data-hd-drawer]');
    this.toggle = this.querySelector('[data-hd-toggle]');
    this.closeBtn = this.querySelector('[data-hd-close]');
    this.panel = this.querySelector('[data-hd-panel]');

    this.toggle?.addEventListener('click', () => this.#setOpen(!this.open));
    this.closeBtn?.addEventListener('click', () => this.#setOpen(false));
    this.querySelector('[data-hd-scrim]')?.addEventListener('click', () => this.#setOpen(false));

    document.addEventListener('keydown', this.#onKeydown);

    // Each group in the drawer is its own disclosure. Several may be open at
    // once: this is a menu to browse, not a set of competing panels.
    this.querySelectorAll('[data-hd-sub]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const item = btn.closest('[data-hd-item]');
        if (!item) return;
        const open = item.classList.toggle('is-open');
        btn.setAttribute('aria-expanded', String(open));
      });
    });

    this.querySelector('[data-hd-cart]')?.addEventListener('click', this.#onCart);

    if (this.hasAttribute('data-sticky')) this.#watchScroll();
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.#onKeydown);
    this.#unlock();
  }

  /**
   * Opens the theme's own cart drawer, which already handles line items,
   * quantity updates and the Section Rendering API. The button stays a real
   * link to /cart, so it still works if the drawer is not on the page.
   */
  #onCart = (event) => {
    const drawer = document.getElementById('cart-drawer');
    if (!drawer || typeof drawer.open !== 'function') return;
    event.preventDefault();
    if (this.open) this.#setOpen(false);
    drawer.open();
  };

  #onKeydown = (event) => {
    if (event.key === 'Escape' && this.open) this.#setOpen(false);
  };

  #setOpen(open) {
    this.open = open;
    this.classList.toggle('is-open', open);
    this.drawer?.toggleAttribute('inert', !open);
    this.drawer?.setAttribute('aria-hidden', String(!open));
    this.toggle?.setAttribute('aria-expanded', String(open));

    if (open) {
      this.#lock();
      // Focus moves into the panel so the keyboard follows the eye.
      window.requestAnimationFrame(() => this.closeBtn?.focus());
    } else {
      this.#unlock();
      this.toggle?.focus();
    }
  }

  /**
   * Freezes the page behind the drawer. The body is pinned at its current
   * offset so the page does not jump to the top, and the scrollbar's width is
   * replaced with padding so the layout behind does not shift.
   */
  #lock() {
    if (this.locked) return;
    this.locked = true;
    this.scrollY = window.scrollY;

    const gutter = window.innerWidth - document.documentElement.clientWidth;
    const body = document.body;
    body.style.position = 'fixed';
    body.style.insetInlineStart = '0';
    body.style.insetInlineEnd = '0';
    body.style.top = `-${this.scrollY}px`;
    if (gutter > 0) body.style.paddingInlineEnd = `${gutter}px`;
  }

  #unlock() {
    if (!this.locked) return;
    this.locked = false;

    const body = document.body;
    for (const prop of ['position', 'insetInlineStart', 'insetInlineEnd', 'top', 'paddingInlineEnd']) {
      body.style.removeProperty(prop.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`));
    }
    window.scrollTo(0, this.scrollY || 0);
  }

  /**
   * Sticky behaviour.
   *
   * The header is only made sticky while the reader is moving up the page, so
   * scrolling down lets it leave naturally and scrolling up brings it straight
   * back. `position: sticky` clamps it to the top the instant it is applied,
   * which is what makes it reappear immediately rather than waiting to be
   * scrolled back into view.
   */
  #watchScroll() {
    let last = window.scrollY;
    let ticking = false;
    // Enough movement to be a deliberate scroll, not momentum jitter.
    const THRESHOLD = 4;

    const update = () => {
      const y = window.scrollY;
      const height = this.offsetHeight || 80;
      const delta = y - last;

      this.classList.toggle('is-stuck', y > height * 0.6);

      if (!this.hasAttribute('data-hide-on-scroll') || this.open) {
        this.classList.add('is-pinned');
      } else if (y <= height) {
        // At the top it belongs in normal flow, above the announcement bar.
        this.classList.remove('is-pinned');
      } else if (delta > THRESHOLD) {
        this.classList.remove('is-pinned');
      } else if (delta < -THRESHOLD) {
        this.classList.add('is-pinned');
      }

      if (Math.abs(delta) > THRESHOLD) last = y;
      ticking = false;
    };

    window.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(update);
      },
      { passive: true }
    );

    update();
  }
}

if (!customElements.get('rana-header')) {
  customElements.define('rana-header', RanaHeader);
}
